import { store } from 'store/reducers'
import { HideOverflow } from 'components/UI'
import { t } from 'translations'
import { Attribute, getAttributesQueryCache } from 'Api'

type GetAttributeNames = (
  attributeArray: (Attribute | { attributeId: string; rank: number })[],
  options?: { delimiter?: string; hideOverflow?: number; key?: string; withRank?: boolean }
) => string | JSX.Element

export const getAttributeNames: GetAttributeNames = (attributeArray = [], { delimiter = ' - ', hideOverflow, ...options } = {}) => {
  const parsedValues = attributeArray.map(attribute => getAttributeName(attribute, options))

  // Return a component to hide overflowing values
  if (hideOverflow) return <HideOverflow parsedValues={parsedValues} hideOverflow={hideOverflow} delimiter={delimiter} />

  return parsedValues.join(delimiter)
}

type GetAttributeName = (attribute: string | Attribute | { attributeId: string; rank: number }, options?: { key?: string; withRank?: boolean }) => string

export const getAttributeName: GetAttributeName = (attribute, { key = 'attributeId', withRank = false } = {}) => {
  const {
    settings: { enableAttributesExpiration }
  } = store.getState() as MStore.RootState
  const data = getAttributesQueryCache()
  const attributesById = data?.attributesById ?? {}

  // If it is not an object, expect an id
  const attributeId = typeof attribute === 'object' ? attribute[key] : attribute

  if (!attributesById[attributeId]) return t('components.ATTRIBUTE_DELETED')

  // Disable withRank if user passes only an attributeId
  if (typeof attribute === 'object' && withRank && attributesById[attributeId].type !== 'alert') {
    return `${attributesById[attributeId].name} (${getAttributeRankName(attributeId, attribute.rank)}${
      'expirationDate' in attribute && attribute.expirationDate && enableAttributesExpiration ? ' - ' + attribute.expirationDate : ''
    })`
  }

  return attributesById[attributeId].name
}

type GetAttributeRankName = (attributeId: string, rank: number) => string
export const getAttributeRankName: GetAttributeRankName = (attributeId, rank) => {
  const data = getAttributesQueryCache()
  const attributesById = data?.attributesById ?? {}
  const attribute = attributesById[attributeId]

  if (!attribute || !attribute.levelsByRank) return t('components.ATTRIBUTE_DELETED')

  if (!attribute.levelsByRank[rank]) return t('components.RANK_DELETED')

  return attribute.levelsByRank[rank].name
}

type GetAttributeType = (attributeId: string) => string
export const getAttributeType: GetAttributeType = attributeId => {
  const data = getAttributesQueryCache()
  const attributesById = data?.attributesById ?? {}

  const attribute = attributesById[attributeId]

  if (!attribute) return t('components.ATTRIBUTE_DELETED')

  return attribute.type
}
